"use client";

import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function FeaturedGuide() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <div className="h-[520px] rounded-2xl bg-[var(--muted)] animate-pulse" />
        </div>
        <div className="lg:col-span-4 flex flex-col gap-6">
          <div className="h-[248px] rounded-xl bg-[var(--muted)] animate-pulse" />
          <div className="h-[248px] rounded-xl bg-[var(--muted)] animate-pulse" />
        </div>
      </div>;
  }
  if (error || !posts || posts.length === 0) {
    return <div className="rounded-2xl border border-[var(--border)] bg-[var(--card)] p-10 text-center">
        <p className="text-base text-[var(--muted-foreground)]">No articles available yet. Check back soon.</p>
      </div>;
  }
  const featured = posts[0];
  const sidePosts = posts.slice(1, 3);
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return "";
    }
  };
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {}
      <article className="lg:col-span-8 group">
        <Link to={featured.link || "#"} className="block">
          <div className="rounded-2xl bg-[var(--card)] border border-[var(--border)] overflow-hidden transition-all duration-[260ms] ease-out hover:-translate-y-[3px] hover:border-[var(--primary)] shadow">
            {}
            <div className="p-6 pb-0">
              <div className="relative rounded-xl bg-[var(--accent)] p-6 overflow-hidden">
                <div className="relative aspect-[16/9] rounded-lg overflow-hidden">
                  {featured.featuredImage ? <img src={featured.featuredImage} alt={featured.title || "Featured article"} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[400ms] ease-out group-hover:scale-[1.02]" /> : <div className="w-full h-full bg-[var(--card)] flex items-center justify-center">
                      <ShieldCheck className="w-8 h-8 text-[var(--primary)]" strokeWidth={1.25} />
                    </div>}
                </div>
              </div>
            </div>

            {}
            <div className="p-8 md:p-10">
              {featured.categories && featured.categories.length > 0 && <div className="mb-5 text-xs font-medium uppercase tracking-[0.14em] text-[var(--primary)]">
                  {featured.categories[0].name}
                </div>}
              <h3 className="font-serif font-normal text-[clamp(1.75rem,2.2vw+0.75rem,2.5rem)] leading-[1.1] tracking-tight text-[var(--foreground)] group-hover:[text-decoration:underline] group-hover:decoration-[var(--primary)] group-hover:underline-offset-[6px] transition-all duration-[260ms]">
                {featured.title}
              </h3>
              {featured.excerpt && <p className="mt-4 text-[var(--foreground)]/80 text-base md:text-lg leading-[1.6] line-clamp-2" dangerouslySetInnerHTML={{
              __html: featured.excerpt
            }} />}
              <div className="mt-6 pt-6 border-t border-[var(--border)] flex items-center justify-between gap-4">
                <div className="text-[13px] text-[var(--muted-foreground)] leading-[1.5]">
                  {featured.author?.name && <span>{"By " + featured.author.name}</span>}
                  {featured.author?.name && featured.date && <span className="mx-2">{"·"}</span>}
                  {featured.date && <span>{formatDate(featured.date)}</span>}
                </div>
                <ArrowUpRight className="w-5 h-5 text-[var(--foreground)] transition-transform duration-[260ms] group-hover:translate-x-1 group-hover:-translate-y-1 shrink-0" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </Link>
      </article>

      {}
      <div className="lg:col-span-4 flex flex-col gap-6">
        {sidePosts.map((post, i) => <article key={post.id} data-index={i} className="group">
            <Link to={post.link || "#"} className="block h-full">
              <div className="rounded-xl bg-[var(--card)] p-5 transition-all duration-[260ms] ease-out hover:-translate-y-[3px] shadow hover:shadow-md">
                <div className="rounded-lg bg-[var(--accent)] p-3 overflow-hidden">
                  <div className="relative aspect-[4/3] rounded-md overflow-hidden">
                    {post.featuredImage ? <img src={post.featuredImage} alt={post.title || "Article"} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[400ms] ease-out group-hover:scale-[1.02]" /> : <div className="w-full h-full bg-[var(--card)] flex items-center justify-center">
                        <ShieldCheck className="w-6 h-6 text-[var(--primary)]" strokeWidth={1.25} />
                      </div>}
                  </div>
                </div>
                <div className="pt-4">
                  {post.categories && post.categories.length > 0 && <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--primary)]">
                      {post.categories[0].name}
                    </div>}
                  <h4 className="text-lg font-semibold leading-[1.3] tracking-tight text-[var(--foreground)] line-clamp-2 group-hover:[text-decoration:underline] group-hover:decoration-[var(--primary)] group-hover:underline-offset-4 transition-all duration-[260ms]">
                    {post.title}
                  </h4>
                  <div className="mt-3 text-[13px] text-[var(--muted-foreground)] leading-[1.5]">
                    {post.author?.name && <span>{post.author.name}</span>}
                    {post.author?.name && post.date && <span className="mx-2">{"·"}</span>}
                    {post.date && <span>{formatDate(post.date)}</span>}
                  </div>
                </div>
              </div>
            </Link>
          </article>)}
      </div>
    </div>;
}
function GuideGrid() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[380px] rounded-xl bg-[var(--muted)] animate-pulse" />)}
      </div>;
  }
  if (error || !posts || posts.length === 0) {
    return null;
  }
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric"
      });
    } catch {
      return "";
    }
  };
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post, i) => <article key={post.id} data-index={i} className="group">
          <Link to={post.link || "#"} className="block h-full">
            <div className="h-full rounded-xl bg-[var(--card)] p-5 transition-all duration-[260ms] ease-out hover:-translate-y-[3px] shadow hover:shadow-md">
              <div className="rounded-lg bg-[var(--accent)] p-3 overflow-hidden">
                <div className="relative aspect-[4/3] rounded-md overflow-hidden">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title || "Article"} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[400ms] ease-out group-hover:scale-[1.02]" /> : <div className="w-full h-full bg-[var(--card)] flex items-center justify-center">
                      <ShieldCheck className="w-6 h-6 text-[var(--primary)]" strokeWidth={1.25} />
                    </div>}
                </div>
              </div>
              <div className="pt-4">
                {post.categories && post.categories.length > 0 && <div className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-[var(--primary)]">
                    {post.categories[0].name}
                  </div>}
                <h4 className="text-lg font-semibold leading-[1.3] tracking-tight text-[var(--foreground)] line-clamp-2 group-hover:[text-decoration:underline] group-hover:decoration-[var(--primary)] group-hover:underline-offset-4 transition-all duration-[260ms]">
                  {post.title}
                </h4>
                {post.excerpt && <p className="mt-2 text-sm text-[var(--foreground)]/75 leading-[1.55] line-clamp-2" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                <div className="mt-4 pt-4 border-t border-[var(--border)] text-[13px] text-[var(--muted-foreground)] leading-[1.5]">
                  {post.author?.name && <span>{post.author.name}</span>}
                  {post.author?.name && post.date && <span className="mx-2">{"·"}</span>}
                  {post.date && <span>{formatDate(post.date)}</span>}
                </div>
              </div>
            </div>
          </Link>
        </article>)}
    </div>;
}
export default function RelatedArticlesGrid() {
  return <section data-section-id="26222" id="relatedarticlesgrid" className="bg-background py-20 md:py-28">
      <div className="mx-auto max-w-[1240px] px-5 md:px-8">
        {}
        <div className="mb-14 md:mb-16 max-w-3xl">
          <div className="mb-5 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-[var(--primary)]">
            <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.25} />
            <span>{"Security Insights"}</span>
          </div>
          <h2 className="font-serif font-normal text-[clamp(2.125rem,3.2vw+0.75rem,3.75rem)] leading-[1.05] tracking-tight text-[var(--foreground)]">
            {"Expert advice for a secure "}
            <em className="italic text-[var(--primary)] font-normal">{"digital life"}</em>
          </h2>
          <p className="mt-5 text-lg text-[var(--foreground)]/75 leading-[1.6] max-w-2xl">
            {"Practical guides on protecting your privacy online — from VPN basics to advanced cybersecurity strategies."}
          </p>
        </div>

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <FeaturedGuide />
        </PostsProvider>

        {}
        <div className="h-[72px]" aria-hidden="true" />

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        offset: 3,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <GuideGrid />
        </PostsProvider>

        {}
        <div className="mt-16 flex justify-center">
          <Link to="/BlogsPage">
            <Button variant="default" size="lg" className="rounded-lg px-7 h-11 bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary)]/90">
              {"View all articles"}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}